import { memo, useState } from "react";

import { incomeReporting } from "byzantine";
import { Button, ContentCard, IconButton, Row } from "@narmi/design_system";
import { Localized, useLocalization } from "@fluent/react";

import { useIncomeReportingCard } from "../hooks";

import styles from "./IncomeReportingProfileCard.module.scss";
import IncomeReportingEmpty from "./IncomeReportingEmpty";
import { IncomeReportingContainer } from "./IncomeReportingContainer";

const {
  INCOME_FIELD_TRANSLATION_KEYS,
  INCOME_FIELD_TRANSLATION_DEFAULTS,
  useIsIncomeReportingEnabled,
  useIncomeReportingFieldValues,
} = incomeReporting;

const IncomeReportingProfileCard = () => {
  const { incomeSource, isFormOpen, openFormHandler, dismissFormHandler } =
    useIncomeReportingCard();
  const getFormattedDisplayValue = useIncomeReportingFieldValues();
  const { l10n } = useLocalization();
  const [isFieldsPrivate, setIsFieldsPrivate] = useState<boolean>(true);
  const { isIncomeReportingEnabled, fieldsToDisplay } =
    useIsIncomeReportingEnabled();

  const togglePrivateFieldsHandler = () => setIsFieldsPrivate((prev) => !prev);

  const togglePrivacyLabel = isFieldsPrivate
    ? l10n.getString(
        "income-reporting-card-show-fields",
        null,
        "Show all fields"
      )
    : l10n.getString(
        "income-reporting-card-hide-fields",
        null,
        "Hide all fields"
      );

  if (!isIncomeReportingEnabled) {
    return null;
  }

  if (!incomeSource) {
    return <IncomeReportingEmpty />;
  }

  return (
    <ContentCard kind="elevated">
      <div className={styles.container}>
        <Row alignItems="center" justifyContent="space-between">
          <Row.Item shrink className={styles["title-container"]}>
            <Localized id="income-reporting-card-title">
              <span className={styles.title}>Income reporting</span>
            </Localized>
            <IconButton
              textSize="l"
              onClick={togglePrivateFieldsHandler}
              name={isFieldsPrivate ? "eye" : "eye-off"}
              label={togglePrivacyLabel}
            />
          </Row.Item>
          <Button
            kind="plain"
            onClick={openFormHandler}
            label={l10n.getString(
              "income-reporting-card-edit-button",
              null,
              "Edit"
            )}
          />
        </Row>

        {fieldsToDisplay.map((field) => (
          <Row key={field} className={styles["field-container"]}>
            <Localized id={INCOME_FIELD_TRANSLATION_KEYS[field]}>
              <span className={styles["field-title"]}>
                {INCOME_FIELD_TRANSLATION_DEFAULTS[field]}
              </span>
            </Localized>
            <span>
              {getFormattedDisplayValue(
                field,
                incomeSource[field],
                isFieldsPrivate
              )}
            </span>
          </Row>
        ))}
      </div>
      <IncomeReportingContainer
        initialValues={incomeSource}
        DialogFormProps={{
          isOpen: isFormOpen,
          onUserDismiss: dismissFormHandler,
        }}
      />
    </ContentCard>
  );
};

export default memo(IncomeReportingProfileCard);
